import { PlusOutlined } from '@ant-design/icons';
import { Avatar, Popover, Space } from 'antd';
import { Fragment } from 'react';
import { ScopeButton, ToggleButton } from '../components/buttons';
import { AvatarSizes, renderAvatar, type AvatarUser } from './renderAvatar';
import { ToggleButtonAnchor } from './ToggleButtonAnchor';

export type AvatarGroupRendererProps = {
  users: AvatarUser[];
  size: AvatarSizes;
  maxCount?: number;
  expandToggleLeft?: boolean;
  onAddUser: () => void;
  addUserLabel: string;
  formatOverflow?: (count: number) => string;
};

export const AvatarGroupRenderer = ({
  users,
  size,
  maxCount,
  expandToggleLeft = false,
  onAddUser,
  addUserLabel,
  formatOverflow = (count) => `+${count}`,
}: AvatarGroupRendererProps) => {
  const effectiveMax = maxCount ?? 2;
  const overflowAvatarStyle = {
    color: 'var(--color-avatar-text, #ffffff)',
    backgroundColor: 'var(--color-avatar-bg, #5a6a6a)',
    border: '1px solid var(--color-avatar-border, #ffffff)',
    fontFamily: 'var(--font-family, Inter, sans-serif)',
    fontWeight: 600,
  };

  const list = users ?? [];
  const visibleUsers = list.slice(0, effectiveMax);
  const hiddenUsers = list.slice(effectiveMax);
  const overflowCount = hiddenUsers.length;

  const userAvatar = (user: AvatarUser) =>
    renderAvatar(
      user.firstName || user.email?.split('@')[0] || '',
      user.lastName || '',
      size,
      user.userUuid,
      user.profilePicture,
    );

  const userKey = (user: AvatarUser, index: number) =>
    user.userUuid || user.accountUuid || user.email || `${user.firstName}-${index}`;

  return (
    <div>
      <Space direction="horizontal" align="center" style={{ columnGap: 0 }}>
        {list.length > 0 ? (
          <>
            <Avatar.Group
              className="scope-avatar-group"
              size={size}
              style={{ marginTop: 5 }}
            >
              {visibleUsers.map((user, index) => (
                <Fragment key={userKey(user, index)}>{userAvatar(user)}</Fragment>
              ))}
              {overflowCount > 0 ? (
                <Popover
                  placement="bottom"
                  trigger="click"
                  destroyOnHidden
                  content={
                    <Space size="small" wrap>
                      {hiddenUsers.map((user, index) => (
                        <Fragment key={userKey(user, effectiveMax + index)}>
                          {userAvatar(user)}
                        </Fragment>
                      ))}
                    </Space>
                  }
                >
                  <Avatar
                    className="scope-avatar"
                    style={overflowAvatarStyle}
                  >
                    {formatOverflow(overflowCount)}
                  </Avatar>
                </Popover>
              ) : null}
            </Avatar.Group>
            <ToggleButtonAnchor
              $expandLeft={expandToggleLeft}
              style={{ marginTop: 5 }}
            >
              <ToggleButton
                className="toggle-button"
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                paddinglft="88px"
                aria-label={addUserLabel}
                onClick={onAddUser}
              >
                <span className="show-on-hover">{addUserLabel}</span>
              </ToggleButton>
            </ToggleButtonAnchor>
          </>
        ) : (
          <ScopeButton
            className="scope-avatar-add-user"
            type="primary"
            onClick={onAddUser}
            width="88px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            style={{ padding: '4px 5px' }}
            icon={<PlusOutlined />}
          >
            <span>{addUserLabel}</span>
          </ScopeButton>
        )}
      </Space>
    </div>
  );
};
